import React, { useCallback, useContext, useMemo, useState } from 'react';
import { z } from 'zod';

import Form from 'components/shared/Form';

import { ItemContext } from 'components/shared/context/ItemContextProvider';
import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import BaseInput from 'components/shared/inputs/BaseInput';
import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import BaseModal from 'components/shared/modals/BaseModal';

import Category from 'schema/category';

import sansanTech from 'services/sansantech';

interface AddTypeModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  category: Category | null;
  resetCategory: () => void;
}

const AddTypeModal: React.FC<AddTypeModalProps> = ({
  isOpen,
  setIsOpen,
  category,
  resetCategory,
}) => {
  const { userState } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);
  const { item, setItem } = useContext(ItemContext);

  const [submitting, setSubmitting] = useState(false);

  const [typeNameValue, setTypeNameValue] = useState<string>('');
  const [isTypeNameValueValid, setIsTypeNameValueValid] =
    useState<boolean>(false);

  const [typePriceValue, setTypePriceValue] = useState<string>('');
  const [isTypePriceValueValid, setIsTypePriceValueValid] =
    useState<boolean>(false);

  const [typeCostValue, setTypeCostValue] = useState<string>('');
  const [isTypeCostValueValid, setIsTypeCostValueValid] =
    useState<boolean>(true);

  const [typeMeasurementUnitValue, setTypeMeasurementUnitValue] =
    useState<string>('');

  const reset = useCallback(() => {
    setTypeNameValue('');
    setIsTypeNameValueValid(false);
    setTypePriceValue('');
    setIsTypePriceValueValid(false);
    setTypeCostValue('');
    setIsTypeCostValueValid(true);
    setTypeMeasurementUnitValue('');
    resetCategory();
  }, [resetCategory]);

  const onSubmit = useCallback(() => {
    setSubmitting(true);
    if (userState.token && item && category) {
      sansanTech
        .createType(userState.token, category.id, {
          name: typeNameValue,
          price: parseFloat(typePriceValue),
          cost: typeCostValue.length ? parseFloat(typeCostValue) : null,
          measurementUnit: typeMeasurementUnitValue.length
            ? typeMeasurementUnitValue
            : null,
        })
        .then((response) => {
          console.log(response);
          setItem?.((currentItem) => {
            const newType = response.data;
            if (currentItem && currentItem.categories) {
              const categoryIndex = currentItem.categories.findIndex(
                (currentItemCategory) => currentItemCategory.id === category.id
              );
              const typeCategory = currentItem.categories[categoryIndex];
              typeCategory.types = typeCategory.types
                ? [...typeCategory.types, newType]
                : [newType];
            }
            return currentItem;
          });
          setSubmitting(false);
          toastDispatch?.({
            type: 'SUCCESS',
            message: 'Added type',
          });
          setIsOpen(false);
          reset();
        })
        .catch((error) => {
          console.log(error);
          setSubmitting(false);
          toastDispatch?.({
            type: 'ERROR',
            message: 'Could not add type',
          });
        });
    } else {
      setSubmitting(false);
      toastDispatch?.({ type: 'ERROR', message: 'No permission to do this' });
    }
  }, [
    reset,
    toastDispatch,
    userState.token,
    setIsOpen,
    item,
    typeNameValue,
    typePriceValue,
    typeCostValue,
    typeMeasurementUnitValue,
    category,
    setItem,
  ]);

  const disabled = useMemo(
    () =>
      !(isTypeNameValueValid && isTypePriceValueValid && isTypeCostValueValid),
    [isTypeCostValueValid, isTypeNameValueValid, isTypePriceValueValid]
  );

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Add type"
      onClose={reset}
    >
      <Form
        header="Add type"
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={disabled}
        submitButtonText="Add type"
        submitButtonWidth="110px"
      >
        <ValidatedBaseInput
          label="type name"
          type="text"
          value={typeNameValue}
          setValue={setTypeNameValue}
          setIsValid={setIsTypeNameValueValid}
          validators={z.string().min(1, { message: 'This field is required' })}
        />
        <ValidatedBaseInput
          label="type price"
          type="number"
          value={typePriceValue}
          setValue={setTypePriceValue}
          setIsValid={setIsTypePriceValueValid}
          validators={z
            .number({
              required_error: 'This field is required',
              invalid_type_error: 'This field is required',
            })
            .positive({ message: 'Must be greater than 0' })}
        />
        <ValidatedBaseInput
          label="type cost"
          type="number"
          value={typeCostValue}
          setValue={setTypeCostValue}
          setIsValid={setIsTypeCostValueValid}
          validators={z
            .number()
            .positive({ message: 'Must be greater than 0' })}
          allowEmptyValue={true}
        />
        <BaseInput
          label="type measurement unit"
          type="text"
          value={typeMeasurementUnitValue}
          setValue={setTypeMeasurementUnitValue}
        />
      </Form>
    </BaseModal>
  );
};

export default AddTypeModal;
